/**
 * ItemFactory - Creates item instances with level-scaled stats and exceptional variants.
 * Equipment items get an instanceData object with scaled stats based on item level.
 * Non-equipment items (consumables, keys, treasures, quest) return null instanceData.
 */
import { ITEM_DEFS } from './ItemDefinitions.js';

const ITEM_LEVEL_SCALE = 0.15; // +15% stats per item level above 1

export const EXCEPTIONAL_TIERS = [
  { prefix: 'Fine',      statMul: 1.15 },
  { prefix: 'Superior',  statMul: 1.25 },
  { prefix: 'Exquisite', statMul: 1.40 }
];

export const EXCEPTIONAL_CHANCES = {
  default: 0.05,
  elite:   0.12,
  boss:    0.20
};

const ATTR_LEVEL_SCALE = 0.08; // +8% per item level for attributes (slower than combat stats)
const ATTR_KEYS = ['str', 'dex', 'int', 'con', 'wis', 'end'];

/**
 * Scale base stats by item level (combat stats + primary attributes).
 * @param {Object} baseStats - { attackPower?, defense?, str?, dex?, int?, con?, wis?, end? }
 * @param {number} itemLevel
 * @returns {Object} scaled stats
 */
function scaleStats(baseStats, itemLevel) {
  const mul = 1 + (itemLevel - 1) * ITEM_LEVEL_SCALE;
  const attrMul = 1 + (itemLevel - 1) * ATTR_LEVEL_SCALE;
  const result = {
    attackPower: Math.round((baseStats.attackPower || 0) * mul),
    defense: Math.round((baseStats.defense || 0) * mul)
  };
  // Scale primary attribute bonuses
  for (const key of ATTR_KEYS) {
    if (baseStats[key]) {
      result[key] = Math.max(1, Math.floor(baseStats[key] * attrMul));
    }
  }
  return result;
}

/**
 * Compute required player level to equip an item.
 * @param {number} itemLevel
 * @returns {number}
 */
function getRequiredLevel(itemLevel) {
  return Math.max(1, itemLevel - 2);
}

/**
 * Roll for an exceptional variant.
 * @param {number} chance - probability [0,1] of being exceptional
 * @returns {{tier: number, prefix: string, statMul: number}|null}
 */
function rollExceptional(chance) {
  if (chance <= 0 || Math.random() > chance) return null;
  // Higher tiers are rarer: tier 0 = 60%, tier 1 = 30%, tier 2 = 10%
  const roll = Math.random();
  let tier;
  if (roll < 0.60)      tier = 0;
  else if (roll < 0.90) tier = 1;
  else                   tier = 2;
  return { tier, prefix: EXCEPTIONAL_TIERS[tier].prefix, statMul: EXCEPTIONAL_TIERS[tier].statMul };
}

/**
 * Create an item instance with level-scaled stats.
 * Returns null for non-equipment items (consumables, keys, treasures, quest items).
 *
 * @param {string} itemId
 * @param {Object} options
 * @param {number} [options.itemLevel=1]
 * @param {number} [options.exceptionalChance=0]
 * @returns {{itemLevel: number, requiredLevel: number, scaledStats: {attackPower: number, defense: number}, exceptional: Object|null}|null}
 */
export function createItemInstance(itemId, { itemLevel = 1, exceptionalChance = 0 } = {}) {
  const def = ITEM_DEFS[itemId];
  if (!def) return null;

  // Only equipment gets instance data
  const equipTypes = ['weapon', 'armor'];
  if (!equipTypes.includes(def.type) || !def.stats) return null;

  const exceptional = rollExceptional(exceptionalChance);
  let scaled = scaleStats(def.stats, itemLevel);

  // Apply exceptional multiplier to all stats
  if (exceptional) {
    const boosted = {
      attackPower: Math.round(scaled.attackPower * exceptional.statMul),
      defense: Math.round(scaled.defense * exceptional.statMul)
    };
    for (const key of ATTR_KEYS) {
      if (scaled[key]) boosted[key] = Math.max(1, Math.round(scaled[key] * exceptional.statMul));
    }
    scaled = boosted;
  }

  return {
    itemLevel,
    requiredLevel: getRequiredLevel(itemLevel),
    scaledStats: scaled,
    exceptional
  };
}

/**
 * Get the display name for an item instance, including exceptional prefix.
 * @param {Object} def - item definition
 * @param {Object|null} instanceData - from createItemInstance
 * @returns {string}
 */
export function getInstanceDisplayName(def, instanceData) {
  if (!def) return '?';
  if (instanceData?.exceptional) {
    return `${instanceData.exceptional.prefix} ${def.name}`;
  }
  return def.name;
}
